import type { ReactNode } from 'react';

/**
 * Meter: il riempimento porta la severita' (token di stato), la traccia e' una
 * versione chiara dello stesso colore, cosi' lo stato si legge su tutta la barra.
 * Il valore e' sempre scritto in cifre accanto all'etichetta: il colore non e'
 * mai l'unico canale che comunica lo stato.
 */
export function Meter({
  label,
  value,
  color,
  threshold,
  suffix = '%',
}: {
  label: string;
  value: number | null;
  color: string;
  /** Riferimento (es. soglia di irrigazione) disegnato come tacca neutra. */
  threshold?: number | null;
  suffix?: string;
}) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="meter" style={{ ['--meter-color' as string]: color }}>
      <span className="meter__label">{label}</span>
      <span className="meter__value">{value == null ? '—' : `${Math.round(value)}${suffix}`}</span>
      <div className="meter__track">
        <div className="meter__fill" style={{ width: `${pct}%` }} />
        {threshold != null && (
          <div
            className="meter__threshold"
            style={{ left: `calc(${Math.max(0, Math.min(100, threshold))}% - 1px)` }}
            title={`Soglia ${Math.round(threshold)}${suffix}`}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Severita' della batteria. Le soglie sono generose perche' sotto il 20 % una
 * cella LiPo cala in fretta e il nodo va ricaricato prima che si spenga.
 */
export function batteryColor(pct: number | null): string {
  if (pct == null) return 'var(--text-muted)';
  if (pct < 15) return 'var(--status-critical)';
  if (pct < 30) return 'var(--status-warning)';
  return 'var(--status-good)';
}

/** Il serbatoio sotto il 15 % non garantisce un ciclo completo di pompa. */
export function waterColor(pct: number | null): string {
  if (pct == null) return 'var(--text-muted)';
  if (pct < 15) return 'var(--status-critical)';
  if (pct < 30) return 'var(--status-warning)';
  return 'var(--series-1)';
}

/** L'umidita' non ha una severita' assoluta: conta solo rispetto alla soglia. */
export function moistureColor(pct: number | null, threshold: number | null): string {
  if (pct == null) return 'var(--text-muted)';
  if (threshold != null && pct < threshold) return 'var(--status-warning)';
  return 'var(--series-1)';
}

export function StatusDot({ online }: { online: boolean }) {
  return (
    <span
      className={`dot ${online ? 'dot--online' : 'dot--offline'}`}
      role="img"
      aria-label={online ? 'online' : 'offline'}
    />
  );
}

export function Badge({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return <span className={`badge${accent ? ' badge--accent' : ''}`}>{children}</span>;
}

export function Notice({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: 'default' | 'warning' | 'error';
}) {
  return <div className={`notice${variant === 'default' ? '' : ` notice--${variant}`}`}>{children}</div>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
